import React, { useState } from 'react';
import { US_STATES } from '../../lib/utils';
import Button from '../../components/Button/Button';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalField } from '../../components/FormField/PortalField';
import { FormGrid } from '../../components/FormField/FormGrid';
import styles from './Chips.module.scss';

export interface AdopterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
  secondaryFirstName: string;
  secondaryLastName: string;
  secondaryEmail: string;
  secondaryPhone: string;
}

export const INITIAL_ADOPTER: AdopterFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  apt: '',
  city: '',
  state: '',
  zip: '',
  secondaryFirstName: '',
  secondaryLastName: '',
  secondaryEmail: '',
  secondaryPhone: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateAdopterForm(adopter: AdopterFormData): Record<string, boolean> {
  const errs: Record<string, boolean> = {};
  if (!adopter.firstName.trim()) errs.firstName = true;
  if (!adopter.lastName.trim()) errs.lastName = true;
  if (!adopter.email.trim() || !EMAIL_PATTERN.test(adopter.email)) errs.email = true;
  if (!adopter.phone.trim()) errs.phone = true;
  if (!adopter.street.trim()) errs.street = true;
  if (!adopter.city.trim()) errs.city = true;
  if (!adopter.state) errs.state = true;
  if (!adopter.zip.trim() || !/^\d{5}$/.test(adopter.zip)) errs.zip = true;
  if (adopter.secondaryEmail.trim() && !EMAIL_PATTERN.test(adopter.secondaryEmail)) errs.secondaryEmail = true;
  return errs;
}

function hasSecondaryContact(adopter: AdopterFormData): boolean {
  return !!(
    adopter.secondaryFirstName ||
    adopter.secondaryLastName ||
    adopter.secondaryEmail ||
    adopter.secondaryPhone
  );
}

/** One-line summary for confirm screens and detail views, or null when there's no secondary contact. */
export function formatSecondaryContact(contact: {
  secondaryFirstName?: string | null;
  secondaryLastName?: string | null;
  secondaryEmail?: string | null;
  secondaryPhone?: string | null;
}): string | null {
  const name = [contact.secondaryFirstName, contact.secondaryLastName].filter(Boolean).join(' ');
  const parts = [name, contact.secondaryEmail, contact.secondaryPhone].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

interface OwnerFormFieldsProps {
  adopter: AdopterFormData;
  errors: Record<string, boolean>;
  onChange: (key: keyof AdopterFormData, value: string) => void;
  emailLocked?: boolean;
}

export default function OwnerFormFields({ adopter, errors, onChange, emailLocked }: OwnerFormFieldsProps) {
  const [secondaryOpened, setSecondaryOpened] = useState(false);
  const showSecondary = secondaryOpened || hasSecondaryContact(adopter);

  function removeSecondary() {
    onChange('secondaryFirstName', '');
    onChange('secondaryLastName', '');
    onChange('secondaryEmail', '');
    onChange('secondaryPhone', '');
    setSecondaryOpened(false);
  }

  return (
    <>
      <FormGrid>
        <PortalField label="First Name *" error="Required" showError={errors.firstName}>
          <PortalInput
            value={adopter.firstName}
            onChange={(e) => onChange('firstName', e.target.value)}
            placeholder="First name"
            hasError={errors.firstName}
          />
        </PortalField>

        <PortalField label="Last Name *" error="Required" showError={errors.lastName}>
          <PortalInput
            value={adopter.lastName}
            onChange={(e) => onChange('lastName', e.target.value)}
            placeholder="Last name"
            hasError={errors.lastName}
          />
        </PortalField>

        <PortalField
          label="Email *"
          error="Valid email required"
          showError={errors.email}
          hint={emailLocked ? 'To change the email, clear the owner and add a new one.' : undefined}
        >
          <PortalInput
            value={adopter.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="Email address"
            type="email"
            hasError={errors.email}
            disabled={emailLocked}
          />
        </PortalField>

        <PortalField label="Phone *" error="Required" showError={errors.phone}>
          <PortalInput
            value={adopter.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            placeholder="Phone number"
            type="tel"
            hasError={errors.phone}
          />
        </PortalField>

        <PortalField label="Street Address *" error="Required" showError={errors.street} fullWidth>
          <PortalInput
            value={adopter.street}
            onChange={(e) => onChange('street', e.target.value)}
            placeholder="Street address"
            hasError={errors.street}
          />
        </PortalField>

        <PortalField label="Apt / Suite">
          <PortalInput
            value={adopter.apt}
            onChange={(e) => onChange('apt', e.target.value)}
            placeholder="Apt / Suite"
          />
        </PortalField>

        <PortalField label="City *" error="Required" showError={errors.city}>
          <PortalInput
            value={adopter.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="City"
            hasError={errors.city}
          />
        </PortalField>

        <PortalField label="State *" error="Required" showError={errors.state}>
          <PortalSelect
            value={adopter.state}
            onChange={(e) => onChange('state', e.target.value)}
            hasError={errors.state}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </PortalSelect>
        </PortalField>

        <PortalField label="Zip Code *" error="5-digit zip required" showError={errors.zip}>
          <PortalInput
            value={adopter.zip}
            onChange={(e) => onChange('zip', e.target.value)}
            placeholder="Zip code"
            hasError={errors.zip}
          />
        </PortalField>
      </FormGrid>

      {showSecondary ? (
        <>
          <div className={styles.secondaryContactHeader}>
            <span className={styles.secondaryContactTitle}>Secondary contact</span>
            <Button variant="link" onClick={removeSecondary}>
              Remove
            </Button>
          </div>
          <FormGrid>
            <PortalField label="First Name">
              <PortalInput
                value={adopter.secondaryFirstName}
                onChange={(e) => onChange('secondaryFirstName', e.target.value)}
                placeholder="First name"
              />
            </PortalField>

            <PortalField label="Last Name">
              <PortalInput
                value={adopter.secondaryLastName}
                onChange={(e) => onChange('secondaryLastName', e.target.value)}
                placeholder="Last name"
              />
            </PortalField>

            <PortalField label="Email" error="Valid email required" showError={errors.secondaryEmail}>
              <PortalInput
                value={adopter.secondaryEmail}
                onChange={(e) => onChange('secondaryEmail', e.target.value)}
                placeholder="Email address"
                type="email"
                hasError={errors.secondaryEmail}
              />
            </PortalField>

            <PortalField label="Phone">
              <PortalInput
                value={adopter.secondaryPhone}
                onChange={(e) => onChange('secondaryPhone', e.target.value)}
                placeholder="Phone number"
                type="tel"
              />
            </PortalField>
          </FormGrid>
        </>
      ) : (
        <Button variant="link" onClick={() => setSecondaryOpened(true)}>
          + Add secondary contact
        </Button>
      )}
    </>
  );
}
